import {
  alpha,
  Box,
  Button,
  ButtonProps,
  Grid,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import React, { useEffect } from "react"
import { VideoType } from "../services/api/video"
import { httpClient } from "../services/http"
import { useTagAPI } from "../services/api/hooks/useTagAPI"
import { useStaticContext } from "../services/static"
import { TagChip } from "./StyledElements"
import { useAuthContext } from "../services/auth"
import { FormatDate, FormatParagraphs } from "./Util"
import { UserRole } from "../services/auth/auth.types"

import { ReactComponent as LikeIcon } from "../assets/svg/like.svg"
import { ReactComponent as KeyIcon } from "../assets/svg/key.svg"
import { ReactComponent as InternetIcon } from "../assets/svg/internet.svg"
import { ReactComponent as LockIcon } from "../assets/svg/lock.svg"
import { ReactComponent as FlameIcon } from "../assets/svg/flame.svg"
import { ReactComponent as DownloadIcon } from "../assets/svg/download.svg"
import { useDLQueueContext } from "../services/dlqueue"

interface LikeButtonProps extends ButtonProps {
  liked: boolean
}

class LikeResponse {
  liked = false
}

const LikeButton = styled(({ ...props }: LikeButtonProps) => <Button {...props} />)(({ theme, liked }) => ({
  backgroundColor: liked ? theme.palette.like.main : "transparent",
  color: liked ? "white" : theme.palette.soft.main,
  border: `1px solid ${theme.palette.like.main}`,
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 12px",
  minHeight: "36px",
  minWidth: "36px",
  height: "fit-content",
  "&:hover": {
    backgroundColor: liked ? theme.palette.like.dark : alpha(theme.palette.like.dark, 0.1),
    borderColor: theme.palette.like.dark,
  },
}))

interface VideoControlsProps {
  video: VideoType
  resolution?: number
  setResolution: React.Dispatch<React.SetStateAction<number>>
}

export const VideoControls = ({ video, resolution, setResolution }: VideoControlsProps): React.ReactElement => {
  const [liked, setLiked] = React.useState<boolean>(false)
  const { role, isAuth } = useAuthContext()
  const { trendingVideos } = useStaticContext()
  const { add: dlQueueAdd } = useDLQueueContext()
  const [availableResolutions, setAvailableResolutions] = React.useState<Array<number>>([])
  const theme = useTheme()

  const { tagsData } = useTagAPI({})

  const isTrending = trendingVideos.some((v) => v.id === video.id)

  useEffect(() => {
    const fetchLike = async () => {
      try {
        const like: LikeResponse = await httpClient.instance.get(`/videos/like/${video.id}`)
        setLiked(like.liked)
      } catch (e) {
        console.error(e)
      }
    }
    fetchLike()
    const fetchResolutions = async () => {
      const response = await fetch(`/api/media/video/${video.id}/resolutions`, { credentials: "same-origin" })
      const data = await response.json()
      setAvailableResolutions(data.sort().reverse())
    }
    fetchResolutions()
  }, [video])

  useEffect(() => {
    if (resolution !== undefined && !availableResolutions.includes(resolution) && availableResolutions.length > 0) {
      setResolution(availableResolutions[0])
    }
  }, [availableResolutions])

  const handleLike = async () => {
    setLiked(!liked)
    try {
      if (liked) {
        httpClient.instance.delete(`/videos/like/${video.id}`)
        if (video.likeCount) video.likeCount--
      } else {
        httpClient.instance.put(`/videos/like/${video.id}`)
        if (video.likeCount) {
          video.likeCount++
        } else {
          video.likeCount = 1
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    video && (
      <Grid container direction="column" sx={{ backgroundColor: "primary.dark", padding: 2, borderRadius: "5px" }}>
        <Grid
          container
          direction="row"
          size={12}
          borderBottom={video.description && video.description.length ? `1px solid ${theme.palette.soft.main}` : "none"}
          sx={{ marginBottom: video.description && video.description.length ? 2 : 0 }}
        >
          <Grid size={{ xs: 12, sm: 7 }}>
            <Typography variant="h5" component="h2">
              {video.title}
              <Box ml={1} component={"span"} display="inline">
                {isTrending && (
                  <Tooltip title={"A la une"}>
                    <FlameIcon width={"1.3rem"} height={"1.3rem"} />
                  </Tooltip>
                )}
                {(role === UserRole.HYRIS || role === UserRole.GEEK) &&
                  (video.privacy === 0 ? (
                    <Tooltip title={"Vidéo publique"}>
                      <InternetIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                    </Tooltip>
                  ) : video.privacy === 2 ? (
                    <Tooltip title={video.gpaRestricted ? "Vidéo cachée aux GPA" : "Vidéo privée"}>
                      <KeyIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                    </Tooltip>
                  ) : (
                    video.privacy === 3 && (
                      <Tooltip title={"Vidéo archivée"}>
                        <LockIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    )
                  ))}
              </Box>
            </Typography>
            <Typography color={"grey"}>
              {FormatDate(video.date) + (video.promotion && video.promotion >= 0 ? " - P" + video.promotion : "")}
            </Typography>
            <Typography color={"grey"}>
              {video.views +
                " vue" +
                (video.views > 1 ? "s" : "") +
                (video.likeCount && isAuth()
                  ? " - " + video.likeCount + " like" + (video.likeCount > 1 ? "s" : "")
                  : "")}
            </Typography>
            <Box gap={1} display="flex" my={1}>
              {tagsData &&
                tagsData
                  .filter((tag) => video.tagIds.includes(tag.id))
                  .map((tag) => <TagChip key={tag.id} tag={tag} />)}
            </Box>
          </Grid>
          {video.processing === 3 && (
            <Grid
              size={{ xs: 12, sm: 5 }}
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                gap: "16px",
              }}
              my={1}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px",
                  minHeight: "36px",
                  height: "fit-content",
                  minWidth: "36px",
                }}
                onClick={() => dlQueueAdd(video)}
              >
                <DownloadIcon style={{ width: "1.3rem", height: "1.3rem", fill: theme.palette.soft.main }} />
              </Button>
              {isAuth() ? (
                <LikeButton variant="contained" color="primary" onClick={() => handleLike()} liked={liked}>
                  <LikeIcon
                    style={{
                      width: "16px",
                      height: "16px",
                      fill: liked ? theme.palette.like.contrastText : theme.palette.soft.main,
                      transition: "all 0.15s ease",
                    }}
                  />
                  {"Like"}
                </LikeButton>
              ) : (
                <></>
              )}
              <Select
                value={resolution}
                onChange={(e) => setResolution(e.target.value as number)}
                variant="standard"
                color="soft"
                sx={{
                  height: "fit-content",
                }}
              >
                {availableResolutions.map((resolution) => (
                  <MenuItem key={resolution} value={resolution}>
                    {`${resolution}p`}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
        </Grid>
        {video.description && video.description.length > 0 && (
          <Grid>
            <Typography>
              <FormatParagraphs content={video.description} />
            </Typography>
          </Grid>
        )}
      </Grid>
    )
  )
}
